/*
 * TIP OF THE DAY — docs/project/tip-of-the-day.md, PR C.
 *
 * One curated learning tip, once per LEARNING day, in a popup hanging under the "?" help icon —
 * and browsable from the same menu whenever the user wants it. The bank is content in the repo
 * (src/main/resources/tips/), the serving order and the per-user pointer are TipService's; this
 * file is the surface.
 *
 * WHAT THE PAGE ARRIVES WITH: the `tips_attr` global — {tip:{id,text,tease,teaseLang}|null, auto,
 * disabled}. `auto` is the server's half of "show it now" (tips not disabled, none read yet
 * today). The client's half is one rule: an onboarding tour on screen WINS. A brand-new user's
 * first screen is the tour's, and two things competing for it is the one thing this must not do.
 *
 * WHY THE CLOSE FOLDS INTO THE "?": the popup is the only thing that ever teaches where tips
 * live. Whichever way it is dismissed — the ×, Esc, a press outside — the frame flies into the
 * help icon (PL.fly, the same transfer effect the add-to-list gestures use), so the next time the
 * user wants that tip they know which button to press. Held to prefers-reduced-motion.
 *
 * THE SECOND LINE (PR D): under the native text, the same tip in the language being learned, with
 * the rich card's own machinery — whole-line audio, per-word hover popups, the orange token border.
 * It is a tease, never a task: a beginner ignores it and loses nothing.
 *
 * ⚠️ WHY ONLY SOME WORDS ARE LIVE. The hover popup asks rich-translate with trusted=true, which
 * skips the AI gate — so hovering a word the pair does NOT already know generates a rich entry and
 * persists a term. Both dictionaries are lemma-only today ("is", "words", "Wörter", "im" have no
 * term row), which would make a fully tokenized tease a junk-term minting machine. The SERVER
 * therefore says which words are safe (TipService.teaseWords = the words that already render from
 * the DB), and everything else here stays inert text. Never widen this client-side.
 */
var TIPS;

(function () {
	"use strict";

	var state = null;      // the `tips_attr` payload, or null = nothing to show, ever
	var popLayer = null;   // the ?-anchored popup's layer handle while it is up

	function el(tag, cls, text) {
		var node = document.createElement(tag);
		if (cls) { node.className = cls; }
		if (text) { node.textContent = text; }
		return node;
	}

	function helpButton() {
		return document.getElementById("helpBookmarkId");
	}

	function reducedMotion() {
		return !!(window.matchMedia
			&& window.matchMedia("(prefers-reduced-motion: reduce)").matches);
	}

	// ── State writes ─────────────────────────────────────────────────────────────

	// Reading a tip is bookkeeping, never load-bearing: a failed stamp costs the user one
	// repeated tip tomorrow, and a message box over a tip they just read would cost more.
	function markShown(tip) {
		if (!tip || tip.shownPosted) { return; }
		tip.shownPosted = true;
		ajaxPost("api/tips/shown", { id: tip.id }, null, null, function () { });
	}

	// The switch, both ways. Not silent, unlike the read stamp above: the user pressed something
	// and expects it to stick, so a failure keeps ajaxPost's message box.
	function setDisabled(off, done) {
		ajaxPost("api/tips/disabled", { value: off }, function () {
			if (state) { state.disabled = off; }
			if (done) { done(); }
		});
	}

	// THE daily switch — "Show a tip each day". One builder, so the popup's foot and the All-tips
	// card carry the SAME control over the same user_attr, not two settings that happen to agree.
	// Only the popup toasts on switching off (toastOff): once it is gone there is no popup left to
	// re-enable from, so it says where the switch also lives; the All-tips copy IS that place.
	function dailySwitch(on, toastOff) {
		var daily = el("button", "tipsDailyClass");
		daily.type = "button";
		daily.setAttribute("role", "switch");
		// Its own class since 2026-09-01: it used to borrow the help menu's .obHubTickClass, and
		// that menu's one ticked row became an icon action, taking the class with it. This switch
		// is still a genuine on/off preference, so the tick stays — and now owns its own rule.
		var tick = el("span", "tipsTickClass");
		daily.appendChild(tick);
		daily.appendChild(el("span", null, getRes("tipsDaily_label")));
		function paint(v) {
			daily.setAttribute("aria-checked", v ? "true" : "false");
			tick.textContent = v ? "✓" : "";
		}
		paint(on);
		daily.addEventListener("click", function () {
			var turningOn = daily.getAttribute("aria-checked") !== "true";
			paint(turningOn);                       // answer the press now…
			setDisabled(!turningOn, function () {   // …the attr catches up, and says so if it can't
				if (!turningOn && toastOff) { showToast(getRes("tipsOff_toast")); }
			});
		});
		return daily;
	}

	// ── The tease: the same tip in the language being learned ────────────────────

	// The pair the page is actually set to. The tease was chosen server-side for the langTo of the
	// page load; if the user has switched language since, its words belong to a dictionary that is
	// no longer open — and a hover would look them up in the WRONG pair, which is the miss that
	// mints. Render is per open, so this is checked at the last possible moment.
	function pairStillMatches(tip) {
		var open = $("#langToValId").attr("data-lang-code") || "";
		return !!tip.teaseLang && open.toUpperCase() === tip.teaseLang.toUpperCase();
	}

	// The tease's words, split by the rich card's OWN tokenizer (main.js exports it) so a tip word
	// and a card word can never disagree about where a word ends. Only the words the server marked
	// live become .term-word — see the header. Everything else, punctuation included, is text.
	function teaseText(tip) {
		var p = el("p", "tipTeaseTextClass");
		p.lang = String(tip.teaseLang || "").toLowerCase();
		// The container a hover's trusted=true is checked against: the server reads this tip's own
		// tease and looks for the word in it (docs/project/go-public-hardening.md §3 F3). Belt and
		// braces over the live-word list above — that already keeps the hover on words the pair
		// holds, so a mint should be unreachable from here, and this is what makes "should be" not
		// depend on the client.
		p.setAttribute("data-tip-id", tip.id || "");
		var segs = window.termWordSegments ? window.termWordSegments(tip.tease) : null;
		if (!segs || !pairStillMatches(tip)) {
			p.textContent = tip.tease;      // inert in one piece — the honest fallback
			return p;
		}
		var live = {};
		(tip.teaseWords || []).forEach(function (w) { live[String(w).toLowerCase()] = true; });
		segs.forEach(function (seg) {
			if (seg.word && live[seg.text.toLowerCase()]) {
				p.appendChild(el("span", "term-word", seg.text));
			} else {
				p.appendChild(document.createTextNode(seg.text));
			}
		});
		return p;
	}

	// The whole line, pronounced. Server-side ElevenLabs, generated once ever per tip and cached as
	// a file from then on, so the first press of the day's speaker waits a beat and every later one
	// is instant. The bar-in-the-button is the phrase rows' own loading affordance (plBtnLoading).
	var teaseAudio = {};   // tip id -> decoded Audio, once fetched

	function playTease(tip, btn) {
		var $btn = $(btn);
		if (teaseAudio[tip.id]) {
			teaseAudio[tip.id].currentTime = 0;
			teaseAudio[tip.id].play();
			return;
		}
		if ($btn.hasClass("loading")) { return; }
		if (window.plBtnLoading) { window.plBtnLoading.start($btn); }
		var done = function () { if (window.plBtnLoading) { window.plBtnLoading.stop($btn); } };
		$.ajax("api/tips/sound", {
			type: "GET",
			data: { id: tip.id },
			dataType: "json",
			// No sound is a normal answer (TTS off), and so is a failed one: a tip is readable
			// without being audible, and a message box over it would cost more than the silence.
			error: done,
			success: function (data) {
				if (!data || !data.url) { done(); return; }
				var audio = new Audio(data.url);
				audio.addEventListener("canplaythrough", function () {
					teaseAudio[tip.id] = audio;
					done();
					audio.play();
				}, { once: true });
				audio.addEventListener("error", done, { once: true });
			}
		});
	}

	// A surface built out of live word tokens has to take their popup with it when it goes: closing
	// on Esc while the pointer rests on a tease word fires no mouseleave, because the word itself
	// stops existing. main.js owns that popup, so it owns the reset too.
	function dropWordPopup() {
		if (window.plResetWordPopup) { window.plResetWordPopup(); }
	}

	function teaseLine(tip) {
		if (!tip.tease) { return null; }
		var row = el("div", "tipTeaseClass");
		var btn = el("button", "pl-btn-icon tipTeaseSoundClass");
		btn.type = "button";
		btn.setAttribute("aria-label", getRes("tipTeaseSound_tooltip"));
		btn.appendChild(el("span", "pl-ic pl-ic-volume"));
		// z:"over-pop" for the same reason the word popup's own buttons use it — this button lives
		// INSIDE a --pl-z-pop surface, and a tooltip in the popup's own band would open behind it.
		PL.tip.attach($(btn), {
			key: "tipTeaseSound_tooltip", z: "over-pop", guided: false,
			on: function (node, name, fn) { $(node).on(name, fn); }
		});
		btn.addEventListener("click", function () { playTease(tip, btn); });
		row.appendChild(btn);
		row.appendChild(teaseText(tip));
		return row;
	}

	// ── The popup ────────────────────────────────────────────────────────────────

	// One tip, in the shape both surfaces use: the native line, and under it the target-language
	// tease when this pair has one. ONE function, so the popup and the All-tips card cannot drift.
	function tipBody(tip) {
		var box = el("div", "tipBodyClass");
		box.appendChild(el("p", "tipTextClass", tip.text));
		var tease = teaseLine(tip);
		if (tease) { box.appendChild(tease); }
		return box;
	}

	/**
	 * Open (or re-open) the daily popup under the "?" — the same popup whether it opened itself
	 * or was asked for from the Help menu. Its foot carries the daily switch, the very control
	 * the All-tips card has: switching the interruption off IS a setting, and showing the setting
	 * beats a one-way "don't show anymore" door. All tips stays reachable from the Help menu.
	 */
	function showTip() {
		if (!state || !state.tip) { return; }
		if (popLayer) { popLayer.close("api"); }

		var btn = helpButton();
		var tip = state.tip;
		var pop = el("div", "tipPopClass");
		pop.setAttribute("role", "dialog");
		pop.setAttribute("aria-label", getRes("tipDay_title"));

		var head = el("div", "tipPopHeadClass");
		var title = el("span", "tipPopTitleClass");
		var bulb = el("span", "tipPopBulbClass", "💡");
		bulb.setAttribute("aria-hidden", "true");
		title.appendChild(bulb);
		title.appendChild(document.createTextNode(getRes("tipDay_title")));
		head.appendChild(title);
		var close = el("button", "tipPopCloseClass", "×");
		close.type = "button";
		close.setAttribute("aria-label", getRes("tipsClose_label"));
		head.appendChild(close);
		pop.appendChild(head);

		pop.appendChild(tipBody(tip));

		var foot = el("div", "tipPopFootClass");
		foot.appendChild(dailySwitch(!state.disabled, true));
		pop.appendChild(foot);

		var focusToken = PL.focus.remember();
		document.body.appendChild(pop);
		anchor(pop, btn);

		popLayer = PL.layers.push({
			el: pop,
			onClose: function () {
				popLayer = null;
				dropWordPopup();
				foldAway(pop, btn);
				if (focusToken) { focusToken.restore({ ifWithin: pop }); }
			}
		});
		close.addEventListener("click", function () {
			if (popLayer) { popLayer.close("api"); }
		});
		close.focus();

		// Stamped on OPEN, not on close: the tip has been put in front of the user, and a tab
		// closed without dismissing it must not serve the same tip again tomorrow.
		markShown(tip);
	}

	// Under the button, right edges aligned — the "?" sits at the right end of the tabs row, so
	// the popup grows leftward and can never leave the viewport (openHub's rule, same anchor).
	function anchor(pop, btn) {
		if (!btn) {
			pop.style.top = "4em";
			pop.style.right = "1em";
			return;
		}
		var r = btn.getBoundingClientRect();
		pop.style.top = (r.bottom + 6) + "px";
		pop.style.right = Math.max(8, window.innerWidth - r.right) + "px";
	}

	// The fold: the frame flies into the "?" as the popup goes. PL.fly measures the source
	// synchronously, so removing the element on the next line is safe.
	function foldAway(pop, btn) {
		if (btn && document.contains(btn) && !reducedMotion()) {
			PL.fly(pop, btn, 320);
		}
		pop.remove();
	}

	// ── All tips ─────────────────────────────────────────────────────────────────

	// The pair the page is showing, straight off the language panel's two value spans — the same
	// `data-lang-code` idiom every other call in the SPA uses. Sent with the All-tips request
	// because a GUEST has no stored pair for the server to read (2026-09-09): the bank is per
	// pair, and for a signed-in user these are exactly the attrs the server would have loaded.
	function pairParams() {
		return {
			termLang: $("#langToValId").attr("data-lang-code") || "",
			defLang:  $("#langFromValId").attr("data-lang-code") || ""
		};
	}

	function showAll() {
		$.ajax("api/tips/all", {
			type: "GET",
			data: pairParams(),
			dataType: "json",
			success: buildAll,
			error: function () { showMessage(getRes("error")); }
		});
	}

	function buildAll(data) {
		var tips = (data && data.tips) || [];
		var overlay = el("div", "msgOverlayClass");
		var box = el("div", "msgBoxClass tipsAllCardClass");
		box.setAttribute("role", "dialog");
		box.setAttribute("aria-modal", "true");
		box.appendChild(el("div", "msgBoxTitleClass", getRes("tipsAll_title")));

		var list = el("div", "tipsAllListClass");
		tips.forEach(function (tip) {
			var item = el("div", "tipsAllItemClass");
			if (tip.id === data.todayId) {
				item.classList.add("tipsAllTodayClass");
				item.appendChild(el("span", "tipsAllBadgeClass", getRes("tipsToday_label")));
			}
			item.appendChild(tipBody(tip));
			list.appendChild(item);
		});
		box.appendChild(list);

		var foot = el("div", "msgBoxFootClass tipsAllFootClass");
		// The daily switch's home surface (decision 6): whatever switched tips off, this row is
		// the visible way back — a kill switch with no way back is not one we ship.
		foot.appendChild(dailySwitch(!data.disabled, false));

		var ok = el("button", "pl-btn-pill obPrimaryBtnClass", getRes("msgOk_button"));
		ok.type = "button";
		foot.appendChild(ok);
		box.appendChild(foot);

		overlay.appendChild(box);
		var focusToken = PL.focus.remember();
		document.body.appendChild(overlay);

		// The layer element is the CARD, not the overlay — a press on the backdrop must read as
		// "outside" and dismiss (the msgbox rule).
		var layer = PL.layers.push({
			el: box,
			onClose: function () {
				dropWordPopup();
				overlay.remove();
				if (focusToken) { focusToken.restore({ ifWithin: box }); }
			}
		});
		ok.addEventListener("click", function () { layer.close("api"); });

		// Tab cycles inside the card; the page behind the backdrop stays visible but unreachable.
		box.addEventListener("keydown", function (e) {
			if (e.key !== "Tab") { return; }
			var items = box.querySelectorAll("button");
			if (!items.length) { e.preventDefault(); return; }
			var first = items[0], last = items[items.length - 1];
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault(); last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault(); first.focus();
			}
		});
		ok.focus();
	}

	// ── Boot ─────────────────────────────────────────────────────────────────────

	// A pair with no applicable tip (and any page that does not inline the global at all) reads
	// as "no tips" rather than as a broken boot — same disarmed-by-absence rule as the tour.
	function parseAttr() {
		if (typeof tips_attr !== "object" || !tips_attr || !tips_attr.tip) { return null; }
		return tips_attr;
	}

	// The tour owns the first screen. States fresh/offered/running all mean a welcome card is
	// up or about to be, and a tip popup underneath it would be two teachers at once. An ABSENT
	// onboarding attr is an existing user, who gets tips normally.
	function tourLive() {
		if (!window.ONBOARDING || !ONBOARDING.tips.armed()) { return false; }
		var s = ONBOARDING.tips.state();
		return s === "fresh" || s === "offered" || s === "running";
	}

	TIPS = {
		// The Help menu's two entries (onboarding.js wires them into the hub).
		showToday: function () { showTip(); },
		showAll: showAll,
		// Whether the menu should offer them at all — a locale with no bank, or a pair nothing
		// applies to, has nothing to show.
		available: function () { return !!state; }
	};

	$(function () {
		state = parseAttr();
		if (!state || !state.auto || tourLive()) { return; }
		// Same small defer as the welcome card: let the app's own ready handlers paint first, so
		// the tip lands on a live page rather than a half-built one.
		setTimeout(function () { showTip(); }, 400);
	});

})();
